import { useEffect, useRef, useCallback } from 'react';

// Simplex-like noise helper (fast 2D)
function createNoise() {
  const perm = new Uint8Array(512);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];

  const grad = [[1,1],[-1,1],[1,-1],[-1,-1],[1,0],[-1,0],[0,1],[0,-1]];
  const F2 = 0.5 * (Math.sqrt(3) - 1);
  const G2 = (3 - Math.sqrt(3)) / 6;

  return function noise2D(x, y) {
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const t = (i + j) * G2;
    const X0 = i - t, Y0 = j - t;
    const x0 = x - X0, y0 = y - Y0;
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;
    const x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
    const ii = i & 255, jj = j & 255;

    let n0 = 0, n1 = 0, n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) { t0 *= t0; const g = grad[perm[ii + perm[jj]] % 8]; n0 = t0 * t0 * (g[0] * x0 + g[1] * y0); }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) { t1 *= t1; const g = grad[perm[ii + i1 + perm[jj + j1]] % 8]; n1 = t1 * t1 * (g[0] * x1 + g[1] * y1); }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) { t2 *= t2; const g = grad[perm[ii + 1 + perm[jj + 1]] % 8]; n2 = t2 * t2 * (g[0] * x2 + g[1] * y2); }

    return 70 * (n0 + n1 + n2);
  };
}

export default function ChromeWave() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1, y: -1 });
  const targetRef = useRef({ x: -1, y: -1 });
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const noiseRef = useRef(null);
  const isTouchRef = useRef(false);
  // Track mobile vs tablet (both are touch, but different flame intensity)
  const isMobileRef = useRef(false);
  // Touch ripples: each {x, y, birth, intensity} — fire bursts on tap
  const ripplesRef = useRef([]);
  // Track if finger is actively touching
  const touchActiveRef = useRef(false);

  const handleMouseMove = useCallback((e) => {
    if (isTouchRef.current) return; // Ignore mouse events from touch
    targetRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleTouchStart = useCallback((e) => {
    isTouchRef.current = true;
    touchActiveRef.current = true;
    const touch = e.touches[0];
    if (touch) {
      targetRef.current = { x: touch.clientX, y: touch.clientY };
      // Tablet gets full fire ripple intensity, mobile gets subtle
      const isNarrow = window.innerWidth < 768;
      ripplesRef.current.push({
        x: touch.clientX,
        y: touch.clientY,
        birth: timeRef.current,
        intensity: isNarrow ? 0.5 : 1.0,
      });
      if (ripplesRef.current.length > (isNarrow ? 3 : 6)) ripplesRef.current.shift();
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    if (touch) {
      targetRef.current = { x: touch.clientX, y: touch.clientY };
      // Tablet gets more ripples like desktop, mobile stays sparse
      const isNarrow = window.innerWidth < 768;
      const threshold = isNarrow ? 0.85 : 0.6;
      const intensity = isNarrow ? 0.3 : 0.6;
      const maxRipples = isNarrow ? 4 : 8;
      if (Math.random() > threshold) {
        ripplesRef.current.push({
          x: touch.clientX,
          y: touch.clientY,
          birth: timeRef.current,
          intensity,
        });
        if (ripplesRef.current.length > maxRipples) ripplesRef.current.shift();
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchActiveRef.current = false;
    // Don't reset target — let the glow linger and fade
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    noiseRef.current = createNoise();

    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const isMobile = isTouch && window.innerWidth < 768;
    const isTablet = isTouch && window.innerWidth >= 768;
    isTouchRef.current = isTouch;
    isMobileRef.current = isMobile;

    // Mobile: subtle, Tablet: intense like desktop, Desktop: default
    const pixelScale = isMobile ? 6 : isTablet ? 5 : 4;

    let w, h;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w / pixelScale);
      canvas.height = Math.floor(h / pixelScale);
    };
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    const render = () => {
      timeRef.current += 0.004;
      const t = timeRef.current;
      const noise = noiseRef.current;
      const cw = canvas.width;
      const ch = canvas.height;

      // Smooth follow — faster on touch for responsiveness
      const followSpeed = touchActiveRef.current ? 0.12 : 0.05;
      mouseRef.current.x += (targetRef.current.x - mouseRef.current.x) * followSpeed;
      mouseRef.current.y += (targetRef.current.y - mouseRef.current.y) * followSpeed;

      const mx = mouseRef.current.x / w;
      const my = mouseRef.current.y / h;
      const hasPointer = mx > -0.5 && my > -0.5;

      // Fade ripples
      const ripples = ripplesRef.current;
      const RIPPLE_LIFESPAN = 3.0; // seconds (in time units)
      ripplesRef.current = ripples.filter(r => (t - r.birth) < RIPPLE_LIFESPAN);

      const imageData = ctx.createImageData(cw, ch);
      const data = imageData.data;

      for (let y = 0; y < ch; y++) {
        for (let x = 0; x < cw; x++) {
          const nx = x / cw;
          const ny = y / ch;

          // Pointer influence (mouse or active touch)
          let pointerInfluence = 0;
          if (hasPointer) {
            const dx = nx - mx;
            const dy = ny - my;
            const dist = Math.sqrt(dx * dx + dy * dy);
            // Wider influence radius on touch (finger is less precise)
            const radius = isTouchRef.current ? 2.2 : 2.8;
            pointerInfluence = Math.max(0, 1 - dist * radius);
            // If touch is not active, fade the influence
            if (isTouchRef.current && !touchActiveRef.current) {
              pointerInfluence *= 0.15;
            }
          }

          // Ripple contributions (fire bursts from taps)
          let rippleGlow = 0;
          for (let ri = 0; ri < ripplesRef.current.length; ri++) {
            const rp = ripplesRef.current[ri];
            const rdx = nx - rp.x / w;
            const rdy = ny - rp.y / h;
            const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
            const age = t - rp.birth;
            // Expanding ring + fading intensity
            const ringRadius = age * 0.15;
            const ringWidth = 0.08 + age * 0.04;
            const ringDist = Math.abs(rdist - ringRadius);
            const fade = Math.max(0, 1 - age / RIPPLE_LIFESPAN);
            const ring = Math.max(0, 1 - ringDist / ringWidth) * fade * rp.intensity;
            // Also a central glow that fades faster
            const centerGlow = Math.max(0, 1 - rdist * 4) * fade * fade * rp.intensity * 0.5;
            rippleGlow += ring + centerGlow;
          }
          // Much lower ripple cap on mobile only (tablet gets full intensity)
          rippleGlow = Math.min(rippleGlow, isMobileRef.current ? 0.4 : 1.0);

          // Multiple flowing noise layers — fire-like turbulence
          const n1 = noise(nx * 3 + t * 0.8, ny * 3 + t * 0.6) * 0.5 + 0.5;
          const n2 = noise(nx * 5 + t * 1.3 + 10, ny * 5 - t * 0.4 + 10) * 0.5 + 0.5;
          const n3 = noise(nx * 1.8 - t * 0.5, ny * 1.8 + t * 0.7) * 0.5 + 0.5;
          const n4 = noise(nx * 7 + t * 2, ny * 7 + t * 1.5 + 5) * 0.5 + 0.5;

          // Fire turbulence mix
          const fire = n1 * 0.4 + n2 * 0.3 + n3 * 0.2 + n4 * 0.1;

          // Chrome metallic highlight near pointer
          const chrome = Math.pow(fire, 2.5) * pointerInfluence;

          // Ambient fire glow — MUCH subtler on mobile, full intensity on tablet/desktop
          const ambientMult = isMobileRef.current ? 0.025 : 0.06;
          const ambientFire = Math.pow(fire, isMobileRef.current ? 2.5 : 1.8) * ambientMult;

          // Ember glow — rises from bottom (reduced on mobile only)
          const emberRise = Math.max(0, (ny - 0.2) * 1.4);
          const ember = fire * emberRise * (isMobileRef.current ? 0.015 : 0.05);

          // Hot spots — random intensities in upper part of screen (reduced on mobile only)
          const hotSpot = Math.pow(n1 * n2, 2) * Math.max(0, 1 - ny * 1.5) * (isMobileRef.current ? 0.02 : 0.08);

          // Ripple fire contribution (reduced on mobile only)
          const rippleFire = rippleGlow * fire * (isMobileRef.current ? 0.06 : 0.18);

          // Combined alpha — lower baseline on mobile only
          const baseAlpha = (isMobileRef.current ? 0.012 : 0.025) + ambientFire + chrome * 0.16 + ember + hotSpot + rippleFire;

          // Fiery color palette
          const hotness = chrome * 3 + fire * 0.2 + hotSpot * 2 + rippleGlow * 1.5;
          const r2 = Math.floor(Math.min(255, (220 + hotness * 35) * baseAlpha * 7));
          const g2 = Math.floor(Math.min(255, (70 + fire * 70 + chrome * 90 + rippleGlow * 40) * baseAlpha * 5.5));
          const b2 = Math.floor(Math.min(255, (15 + chrome * 50 + rippleGlow * 20) * baseAlpha * 3.5));

          const idx = (y * cw + x) * 4;
          data[idx] = Math.min(255, r2);
          data[idx + 1] = Math.min(255, g2);
          data[idx + 2] = Math.min(255, b2);
          data[idx + 3] = Math.floor(Math.min(255, baseAlpha * 320));
        }
      }

      ctx.putImageData(imageData, 0, 0);
      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <canvas
      ref={canvasRef}
      id="chrome-wave-canvas"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        imageRendering: 'auto',
      }}
    />
  );
}
